import React, { useEffect, useState } from 'react';
import { toast } from 'sonner';
import { Mail, Loader2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { bapi, fmtDate } from '@/lib/billingApi';
import { Segmented } from '@/components/admin/Segmented';
import { VideoFunnel } from './VideoFunnel';
import { WatchCompare } from './WatchCompare';
import { SignupSources } from './SignupSources';
import { Upgrades } from './Upgrades';
import { VisitorsChart } from './VisitorsChart';
import { TopPages, Breakdown } from './VisitorBreakdowns';
import { AdLinks } from './AdLinks';
import { BracketFunnel } from './BracketFunnel';

const RANGES = [['7', '7 days'], ['30', '30 days'], ['90', '90 days'], ['0', 'All time']];

const VisitorSections = ({ v }) => (
  <>
    <VisitorsChart v={v} />
    <TopPages pages={v.top_pages} />
    <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
      <Breakdown title="Where visitors came from" rows={v.sources} total={v.totals.all_visitors} prefix="growth-visit-source" testId="growth-visit-sources" />
      <Breakdown title="Device" rows={v.devices} total={v.totals.all_visitors} prefix="growth-device" testId="growth-devices" />
      <Breakdown title="Country" rows={v.countries} total={v.totals.all_visitors} prefix="growth-country" testId="growth-countries"
        note={<>Approximate, from the visitor's internet connection. <a href="https://db-ip.com" target="_blank" rel="noreferrer" className="underline">IP Geolocation by DB-IP</a></>} />
    </div>
  </>
);

export const GrowthTab = () => {
  const [days, setDays] = useState('30');
  const [r, setR] = useState(null);
  const [v, setV] = useState(null);
  const [sending, setSending] = useState(false);

  useEffect(() => {
    setR(null);
    setV(null);
    bapi('/admin/growth', { query: { days } }).then(setR).catch((e) => toast.error(e.message));
    bapi('/admin/growth/visitors', { query: { days } }).then(setV).catch((e) => toast.error(e.message));
  }, [days]);

  const emailMe = async () => {
    setSending(true);
    try {
      const d = await bapi('/admin/growth/email-me', { method: 'POST' });
      toast.success(`Last week's summary sent to ${d.to}`);
    } catch (e) { toast.error(e.message); }
    setSending(false);
  };

  return (
    <div className="flex flex-col gap-6" data-testid="growth-tab">
      <div className="flex flex-col lg:flex-row lg:items-end justify-between gap-4">
        <div>
          <h2 className="text-2xl font-bold text-slate-900">Growth</h2>
          <p className="text-sm text-slate-500" data-testid="growth-tracking-note">
            {r?.tracking_since ? `Visitor and video tracking started ${fmtDate(r.tracking_since)}.` : 'Visitor and video tracking starts with the next landing page visit.'}
            {' '}Signups and upgrades go back to launch. A summary is emailed to admins every Monday.
          </p>
        </div>
        <div className="flex flex-col sm:flex-row sm:items-center gap-3">
          <div className="sm:w-[320px]"><Segmented value={days} options={RANGES} onChange={setDays} testId="growth-range" /></div>
          <Button variant="outline" onClick={emailMe} disabled={sending} data-testid="growth-email-me-btn">
            {sending ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Mail className="w-4 h-4 mr-2" />}Email me last week
          </Button>
        </div>
      </div>
      {!r || !v ? (
        <div className="py-16 flex justify-center" data-testid="growth-loading"><Loader2 className="w-8 h-8 text-[#00247D] animate-spin" /></div>
      ) : (
        <>
          <VisitorSections v={v} />
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            <VideoFunnel v={r.video} />
            <WatchCompare w={r.watch_compare} />
          </div>
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            <SignupSources s={r.signups} />
            <Upgrades u={r.upgrades} test={r.test_mode} />
          </div>
          {r.bracket && <BracketFunnel b={r.bracket} />}
          <AdLinks />
        </>
      )}
    </div>
  );
};
